import React, { useEffect, useState } from "react";
import { Button, Drawer, Descriptions, Table, Tag, Spin, Badge, Card, Divider, Tooltip, Typography } from "antd";

import { formatDate } from "../../../../utils/dateUtils";
import { settlementApi } from "../../../../api/settlementApi";
import { CURRENCY_SYMBOLS } from "../../constants";
import { getLineItemLifecycleStatus, getReplacementId, getReplacementLabel, getVoidReason, isLineItemActive } from "../../utils/revisionDisplay";

const { Text } = Typography;

const toFiniteNumber = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
};

const getCurrencySymbol = (currency) => CURRENCY_SYMBOLS[currency?.toUpperCase()] || currency || "";

const formatQuantityWithUnit = (quantity, unit) => {
    const number = toFiniteNumber(quantity);
    if (number === null) return "-";
    const quantityText = number.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    return unit ? `${quantityText} ${unit}` : quantityText;
};

const formatUnitPrice = (unitPrice, currency) => {
    const number = toFiniteNumber(unitPrice);
    if (number === null || number === 0) return "-";
    const currencySymbol = getCurrencySymbol(currency);
    const separator = currencySymbol && /[A-Za-z]$/.test(currencySymbol) ? " " : "";
    return `${currencySymbol}${separator}${number.toLocaleString("en-US", {
        minimumFractionDigits: 6,
        maximumFractionDigits: 6,
    })}`;
};

const InvoiceDetailDrawer = ({ visible, onClose, invoiceId }) => {
    const [loading, setLoading] = useState(false);
    const [invoice, setInvoice] = useState(null);
    const [activeInvoiceId, setActiveInvoiceId] = useState(null);

    useEffect(() => {
        if (visible && invoiceId) {
            setActiveInvoiceId(invoiceId);
        } else {
            setActiveInvoiceId(null);
            setInvoice(null);
        }
    }, [visible, invoiceId]);

    useEffect(() => {
        if (visible && activeInvoiceId) {
            fetchInvoiceDetail(activeInvoiceId);
        }
    }, [visible, activeInvoiceId]);

    const fetchInvoiceDetail = async (targetInvoiceId) => {
        setLoading(true);
        try {
            const response = await settlementApi.invoices.getInvoiceDetail(targetInvoiceId);
            if (response.data) {
                setInvoice(response.data);
            }
        } catch (error) {
            console.error("Failed to fetch invoice detail:", error);
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            DRAFT: "default",
            PENDING_MATCHING: "processing",
            PARTIALLY_RECONCILED: "warning",
            RECONCILED: "success",
            DISPUTED: "error",
            PAID: "success",
        };
        return <Badge status={statusMap[status] || "default"} text={status} />;
    };

    const getLifecycleTag = (status) => {
        const normalized = String(status || "ACTIVE").toUpperCase();
        const config = {
            ACTIVE: { color: "success", label: "有效" },
            VOID: { color: "default", label: "已作废" },
            VOIDED: { color: "default", label: "已作废" },
            REPLACED: { color: "orange", label: "已替代" },
        }[normalized] || { color: "default", label: normalized };
        return <Tag color={config.color}>{config.label}</Tag>;
    };

    const [expandedRowKeys, setExpandedRowKeys] = useState([]);

    // Reset expanded rows when invoice changes
    useEffect(() => {
        setExpandedRowKeys([]);
    }, [activeInvoiceId]);

    const handleExpand = (expanded, record) => {
        if (expanded) {
            setExpandedRowKeys((keys) => [...keys, record.id]);
        } else {
            setExpandedRowKeys((keys) => keys.filter((k) => k !== record.id));
        }
    };

    const handleRowClick = (record) => {
        if (record.allocations && record.allocations.length > 0) {
            const isExpanded = expandedRowKeys.includes(record.id);
            handleExpand(!isExpanded, record);
        }
    };

    const columns = [
        {
            title: "描述",
            dataIndex: "description",
            key: "description",
            ellipsis: true,
            render: (text, record) => (
                <Tooltip title={getVoidReason(record) || text}>
                    <Text delete={!isLineItemActive(record)} type={!isLineItemActive(record) ? "secondary" : undefined}>
                        {text}
                    </Text>
                </Tooltip>
            ),
        },
        {
            title: "费用类型",
            dataIndex: "cost_type",
            key: "cost_type",
            render: (text) => <Tag>{text}</Tag>,
        },
        {
            title: "数量",
            dataIndex: "quantity",
            key: "quantity",
            render: (val, record) => formatQuantityWithUnit(val, record.unit),
        },
        {
            title: "单价 (原币)",
            dataIndex: "original_unit_price",
            key: "original_unit_price",
            align: "right",
            render: (val, record) => {
                const priceText = formatUnitPrice(val, invoice?.original_currency);
                if (priceText === "-") return "-";
                return `${formatQuantityWithUnit(record.quantity, record.unit)} x ${priceText}`;
            },
        },
        {
            title: "总额 (原币)",
            dataIndex: "original_total_amount",
            key: "original_total_amount",
            align: "right",
            render: (val) => (val ? Number(val).toFixed(2) : "-"),
        },
        {
            title: "总额 (本位币)",
            dataIndex: "base_total_amount",
            key: "base_total_amount",
            align: "right",
            render: (val, record) => {
                const amount = record.functional_total_amount ?? val;
                return amount ? Number(amount).toFixed(2) : "-";
            },
        },
        {
            title: "分摊类型",
            dataIndex: "allocation_type",
            key: "allocation_type",
            render: (text) => (text === "DIRECT" ? "直接成本" : "待分摊"),
        },
        {
            title: "生命周期",
            dataIndex: "lifecycle_status",
            key: "lifecycle_status",
            render: (_, record) => getLifecycleTag(getLineItemLifecycleStatus(record)),
        },
    ];

    const invoiceReplacementId = getReplacementId(invoice);
    const invoiceReplacementLabel = getReplacementLabel(invoice);
    const invoiceReplacementText = invoiceReplacementLabel && String(invoiceReplacementLabel) !== String(invoiceReplacementId) ? `已被 ${invoiceReplacementLabel} 替代` : "查看替代单";
    const invoiceVoidReason = getVoidReason(invoice);
    const invoiceFunctionalAmount = {
        amount: invoice?.functional_total_amount ?? invoice?.base_total_amount,
        currency: invoice?.functional_currency ?? invoice?.base_currency,
    };

    return (
        <Drawer title="账单详情" width={1200} placement="right" onClose={onClose} open={visible}>
            <Spin spinning={loading}>
                {invoice && (
                    <>
                        <Card title="基本信息" bordered={false} className="mb-4">
                            <Descriptions column={2}>
                                <Descriptions.Item label="账单号">{invoice.invoice_number}</Descriptions.Item>
                                <Descriptions.Item label="供应商">{invoice.supplier?.name || invoice.supplier_id}</Descriptions.Item>
                                <Descriptions.Item label="状态">{getStatusBadge(invoice.status)}</Descriptions.Item>
                                <Descriptions.Item label="生命周期">{getLifecycleTag(invoice.lifecycle_status)}</Descriptions.Item>
                                <Descriptions.Item label="来源">{invoice.source_type}</Descriptions.Item>
                                <Descriptions.Item label="账单日期">{formatDate(invoice.issue_date)}</Descriptions.Item>
                                <Descriptions.Item label="到期日期">{formatDate(invoice.due_date)}</Descriptions.Item>
                                <Descriptions.Item label="原币金额">
                                    {Number(invoice.original_total_amount).toFixed(2)} {invoice.original_currency}
                                </Descriptions.Item>
                                <Descriptions.Item label="本位币金额">
                                    {Number(invoiceFunctionalAmount.amount || 0).toFixed(2)} {invoiceFunctionalAmount.currency} (汇率: {invoice.system_functional_rate ?? invoice.exchange_rate})
                                </Descriptions.Item>
                                {invoiceVoidReason && <Descriptions.Item label="作废原因">{invoiceVoidReason}</Descriptions.Item>}
                                {invoiceReplacementId && (
                                    <Descriptions.Item label="替代新单">
                                        <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setActiveInvoiceId(invoiceReplacementId)}>
                                            {invoiceReplacementText}
                                        </Button>
                                    </Descriptions.Item>
                                )}
                            </Descriptions>
                            {invoice.remarks && (
                                <>
                                    <Divider />
                                    <Descriptions column={1}>
                                        <Descriptions.Item label="备注">{invoice.remarks}</Descriptions.Item>
                                    </Descriptions>
                                </>
                            )}
                        </Card>

                        <Card title="费用明细" bordered={false} className="mt-4">
                            <Table
                                dataSource={invoice.line_items || []}
                                columns={columns}
                                rowKey="id"
                                pagination={false}
                                size="small"
                                onRow={(record) => ({
                                    onClick: () => handleRowClick(record),
                                    style: {
                                        cursor: record.allocations && record.allocations.length > 0 ? "pointer" : "default",
                                        background: isLineItemActive(record) ? undefined : "#fafafa",
                                        color: isLineItemActive(record) ? undefined : "#8c8c8c",
                                    },
                                })}
                                expandable={{
                                    expandedRowKeys: expandedRowKeys,
                                    onExpand: handleExpand,
                                    expandedRowRender: (record) => {
                                        if (!record.allocations || record.allocations.length === 0) return null;
                                        return (
                                            <div style={{ margin: 0 }}>
                                                <p style={{ marginBottom: 8, fontWeight: "bold" }}>分摊明细:</p>
                                                <Table
                                                    dataSource={record.allocations}
                                                    columns={[
                                                        { title: "分摊目标", dataIndex: "target", key: "target" },
                                                        {
                                                            title: "分摊金额",
                                                            dataIndex: "amount",
                                                            key: "amount",
                                                            render: (val) => (val ? Number(val).toFixed(2) : "-"),
                                                        },
                                                        {
                                                            title: "比例",
                                                            dataIndex: "percentage",
                                                            key: "percentage",
                                                            render: (val) => (val ? `${val}%` : "-"),
                                                        },
                                                    ]}
                                                    pagination={false}
                                                    size="small"
                                                    bordered
                                                    rowKey={(item, index) => index}
                                                />
                                            </div>
                                        );
                                    },
                                    rowExpandable: (record) => record.allocations && record.allocations.length > 0,
                                }}
                            />
                        </Card>
                    </>
                )}
            </Spin>
        </Drawer>
    );
};

export default InvoiceDetailDrawer;
